import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { EmptyState } from 'components/empty_state/EmptyState';
import {
    CellContentWrapper,
    ContentWrapper,
    ExpandIcon,
    SpecialStateCell,
    SpecialStateRow,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderRow,
    TableRow,
} from 'components/infinite_data_source_table/styled';
import { TableSortIcon } from 'components/infinite_data_source_table/TableSortIcon';
import {
    Column,
    InfiniteDataSourceTableProps,
    SortDirection,
} from 'components/infinite_data_source_table/types';
import { InfiniteScrollTableBody } from 'components/infinite_scroll/InfiniteScrollTableBody';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { JSX, memo, useCallback, useMemo, useState } from 'react';

function getNextSortDirection(currentSort?: DataSourceSort): SortDirection | undefined {
    if (!currentSort) {
        return 'asc';
    }
    return currentSort.dir === 'asc' ? 'desc' : undefined;
}

function InfiniteDataSourceTable<TParentData, TFilter, TChildData = never>({
    dataSource,
    columns,
    rowKey,
    sort = [],
    onSort,
    components = {},
    noResultsMessage = 'No results found',
    className,
    getNestedRows,
    defaultExpandedRows = [],
    onRowExpand,
    indentSize = 1,
    showEmptyState = false,
}: InfiniteDataSourceTableProps<TParentData, TFilter, TChildData>) {
    const {
        TableContainer: CustomTableContainer = TableContainer,
        Table: CustomTable = Table,
        TableHead: CustomTableHead = TableHead,
        TableHeader: CustomTableHeader = TableHeader,
        TableBody: CustomTableBody = TableBody,
        TableRow: CustomTableRow = TableRow,
        TableHeaderRow: CustomTableHeaderRow = TableHeaderRow,
        TableCell: CustomTableCell = TableCell,
    } = components;

    const [expandedRows, setExpandedRows] = useState<Set<string>>(
        () => new Set(defaultExpandedRows),
    );

    const handleSort = useCallback(
        (column: Column<TParentData | TChildData>) => {
            if (!column.sortable || !column.sortField || !onSort) {
                return;
            }

            const currentSort = sort.find((s) => s.field === column.sortField);
            const newDirection = getNextSortDirection(currentSort);
            const newSort = newDirection
                ? [{ field: column.sortField, dir: newDirection }]
                : [];
            onSort(newSort);
        },
        [sort, onSort],
    );

    const getKey = useCallback(
        (row: TParentData | TChildData): string => {
            return rowKey(row);
        },
        [rowKey],
    );

    const handleRowClick = useCallback(
        (row: TParentData | TChildData, level: number) => {
            if (!getNestedRows) {
                return;
            }

            const key = getKey(row);
            // Only parent rows (level 0) can have nested rows
            if (level === 0) {
                const nestedRows = getNestedRows(row as TParentData);
                if (!nestedRows.length) {
                    return;
                }

                setExpandedRows((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) {
                        next.delete(key);
                    } else {
                        next.add(key);
                    }
                    onRowExpand?.(key, !prev.has(key));
                    return next;
                });
            }
        },
        [getKey, getNestedRows, onRowExpand],
    );

    const renderRow = useMemo(() => {
        const renderCell = (
            column: Column<TParentData | TChildData>,
            rowData: TParentData | TChildData,
            isExpandable?: boolean,
            isExpanded?: boolean,
        ) => {
            const content = column.render({ rowData });
            if (column.id === columns[0].id) {
                return (
                    <CellContentWrapper>
                        {isExpandable ? (
                            <ExpandIcon icon={faChevronRight} $isExpanded={isExpanded} />
                        ) : null}
                        {typeof content === 'string'
                            ? content.replace(/\\/g, '\\\u200B')
                            : content}
                    </CellContentWrapper>
                );
            }
            return content;
        };

        // not expandable
        if (!getNestedRows) {
            return (row: TParentData | TChildData): JSX.Element => (
                <CustomTableRow key={getKey(row)} onClick={() => handleRowClick(row, 0)}>
                    {columns.map((column) => (
                        <CustomTableCell key={column.id}>
                            {renderCell(column, row)}
                        </CustomTableCell>
                    ))}
                </CustomTableRow>
            );
        }

        // expandable
        const renderRowImpl = (
            row: TParentData | TChildData,
            level: number,
        ): JSX.Element[] => {
            const key = getKey(row);
            // Only parent rows (level 0) can have nested rows
            const nestedRows =
                level === 0 ? getNestedRows(row as TParentData) : undefined;
            const isExpandable = !!nestedRows?.length;
            const isExpanded = expandedRows.has(key);

            const elements = [
                <CustomTableRow
                    key={key}
                    onClick={() => handleRowClick(row, level)}
                    $isExpandable={isExpandable}
                    $isExpanded={isExpanded}
                    $nestingLevel={level}
                >
                    {columns.map((column) => (
                        <CustomTableCell
                            key={column.id}
                            style={
                                level > 0 && column.id === columns[0].id
                                    ? { paddingLeft: `${level * indentSize}rem` }
                                    : undefined
                            }
                        >
                            {renderCell(column, row, isExpandable, isExpanded)}
                        </CustomTableCell>
                    ))}
                </CustomTableRow>,
            ];

            if (isExpanded && nestedRows) {
                nestedRows.forEach((nestedRow) => {
                    elements.push(...renderRowImpl(nestedRow, level + 1));
                });
            }

            return elements;
        };

        return (row: TParentData): JSX.Element[] => renderRowImpl(row, 0);
    }, [
        columns,
        expandedRows,
        getKey,
        getNestedRows,
        handleRowClick,
        indentSize,
        CustomTableCell,
        CustomTableRow,
    ]);

    return (
        <SpinnerArea isLoading={dataSource.isLoading} className="overflow-y-hidden">
            <CustomTableContainer className={className}>
                <CustomTable>
                    {(columns.length > 0 || showEmptyState) && (
                        <>
                            <CustomTableHead>
                                <CustomTableHeaderRow>
                                    {columns.map((column) => {
                                        const sortConf = sort.find(
                                            (s) => s.field === column.sortField,
                                        );
                                        return (
                                            <CustomTableHeader
                                                key={column.id}
                                                onClick={() => handleSort(column)}
                                                $sortable={column.sortable}
                                                $isSorted={!!sortConf}
                                            >
                                                <ContentWrapper>
                                                    {column.header()}
                                                    {column.sortable && (
                                                        <TableSortIcon
                                                            isSorted={!!sortConf}
                                                            direction={sortConf?.dir}
                                                        />
                                                    )}
                                                </ContentWrapper>
                                            </CustomTableHeader>
                                        );
                                    })}
                                </CustomTableHeaderRow>
                            </CustomTableHead>
                            <InfiniteScrollTableBody
                                onLoadMore={dataSource.loadNext}
                                isLoading={dataSource.isLoading}
                                enabled={dataSource.hasNext ?? false}
                                className={CustomTableBody.toString()}
                            >
                                {(!dataSource.results?.length || showEmptyState) &&
                                !dataSource.isLoading ? (
                                    <SpecialStateRow>
                                        <SpecialStateCell colSpan={columns.length}>
                                            <EmptyState message={noResultsMessage} />
                                        </SpecialStateCell>
                                    </SpecialStateRow>
                                ) : (
                                    dataSource.results?.map((row) =>
                                        Array.isArray(renderRow(row))
                                            ? renderRow(row)
                                            : [renderRow(row)],
                                    )
                                )}
                            </InfiniteScrollTableBody>
                        </>
                    )}
                </CustomTable>
            </CustomTableContainer>
        </SpinnerArea>
    );
}

const memoInfiniteDataSourceTable = memo(
    InfiniteDataSourceTable,
) as typeof InfiniteDataSourceTable;

export { memoInfiniteDataSourceTable as InfiniteDataSourceTable };
